import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import '../../../styles/eventsCheckin.css';
import { formatEventDate } from '../eventConstants';
import { passFileName } from './checkin';
import { qrPath, qrMatrix, qrPixels, qrSvgText } from './qr';

// Module 19 (BL-380) G5 - a guest's check-in pass: the QR (only the opaque pass code) and, printed beside it, who and what it is for.
// The pass is always black on white (on screen and on paper): a QR needs that contrast to scan.

export function QrImage({ token, label, size = 176, testId = 'pass-qr' }) {
    const { d, size: modules } = qrPath(qrMatrix(token));
    return (
        <svg role="img" aria-label={label} viewBox={`0 0 ${modules} ${modules}`} width={size} height={size} shapeRendering="crispEdges" className="ia-pass__qr" data-testid={testId} data-pass-code={token}>
            <rect width={modules} height={modules} fill="#FFFFFF" />
            <path d={d} fill="#000000" />
        </svg>
    );
}

export function PassCard({ pass, event, qrSize = 176 }) {
    const when = [formatEventDate(event?.eventDate || event?.event_date), event?.venueName || event?.venue_name].filter(Boolean).join(' · ');
    return (
        <article className="ia-pass" data-testid="pass-card" data-guest-id={pass.guestId}>
            <QrImage token={pass.token} label={`Check-in pass for ${pass.name}`} size={qrSize} />
            <div className="ia-pass__text">
                <div className="ia-pass__name">{pass.name}</div>
                {pass.hostName && <div className="ia-pass__line">Plus-one of {pass.hostName}</div>}
                <div className="ia-pass__line ia-pass__event">{event?.eventName || event?.event_name || ''}</div>
                {when && <div className="ia-pass__line">{when}</div>}
                <div className="ia-pass__hint">Show this at the door</div>
            </div>
        </article>
    );
}

// What gets printed: rendered straight into <body>, hidden on screen, the only thing on paper while printing (eventsCheckin.css).
export function PassPrintSheet({ passes, event }) {
    if (typeof document === 'undefined') return null;
    return createPortal(
        <div id="ia-pass-print-root" className="ia-pass-print" data-testid="pass-print-root" aria-hidden="true">
            {passes.map((pass) => <PassCard key={pass.guestId} pass={pass} event={event} qrSize={150} />)}
        </div>,
        document.body,
    );
}

// Print only the passes: the page is marked while the print dialog is open.
export function printPasses(win = window) {
    const body = win.document.body;
    body.classList.add('ia-printing-passes');
    const done = () => { body.classList.remove('ia-printing-passes'); win.removeEventListener('afterprint', done); };
    win.addEventListener('afterprint', done);
    try { win.print(); } catch { /* a browser without printing does nothing */ }
    // some browsers return from print() before 'afterprint' and some never send it: clear the mark either way
    win.setTimeout(done, 1000);
}

// Keeps the mark from outliving the popup.
export function usePrintCleanup() {
    useEffect(() => () => { if (typeof document !== 'undefined') document.body.classList.remove('ia-printing-passes'); }, []);
}

function save(blob, fileName) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
}

// One guest's pass as a picture file: a PNG when the browser can draw one, else the same QR as an SVG.
export function downloadPass(pass) {
    try {
        const { data, width } = qrPixels(pass.token, { scale: 10, margin: 4 });
        const canvas = document.createElement('canvas');
        const context = typeof canvas.getContext === 'function' && !/jsdom/i.test(navigator.userAgent || '') ? canvas.getContext('2d') : null;
        if (context && typeof canvas.toBlob === 'function') {
            const textHeight = 56;
            canvas.width = width; canvas.height = width + textHeight;
            context.fillStyle = '#FFFFFF';
            context.fillRect(0, 0, canvas.width, canvas.height);
            context.putImageData(new ImageData(data, width, width), 0, 0);
            context.fillStyle = '#000000';
            context.font = '600 22px "Exo 2", sans-serif';
            context.textAlign = 'center';
            context.fillText(String(pass.name || '').slice(0, 40), width / 2, width + 34);
            canvas.toBlob((blob) => { if (blob) save(blob, passFileName(pass.name, 'png')); }, 'image/png');
            return 'png';
        }
    } catch {
        // fall back to the SVG below
    }
    try {
        save(new Blob([qrSvgText(pass.token, { title: `Check-in pass for ${pass.name}` })], { type: 'image/svg+xml' }), passFileName(pass.name, 'svg'));
        return 'svg';
    } catch {
        return '';
    }
}
